// ManageManufacturersPage.tsx
import React, { useState, useMemo } from "react";
import { ArrowLeft, Plus, Search } from "lucide-react";
import { FormField, FormService } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import { GenericTable } from "@shared/components/GenericTable";
import {
  useCreateMeterManufacturer,
  useUpdateMeterManufacturer,
  useMeterManufacturers,
  useManufacturerType,
} from "../hooks";
import {
  ManufacturerData,
  CreateMeterManufacturerPayload,
  UpdateMeterManufacturerPayload,
} from "../types";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useNavigate } from "react-router-dom";
import { useDeleteManufacture } from "@features/mx/settings/hooks";
import { Button } from "@shared/ui/button";

interface ManageManufacturersPageProps {
  onBack: () => void;
  remoteUtilityId?: number; // Add this prop to pass the utility ID
}

export const ManageManufacturersPage: React.FC<
  ManageManufacturersPageProps
> = ({ onBack }) => {
  const navigate = useNavigate();
  // Default value for demo
  const remoteUtilityId = getRemoteUtilityId();
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingManufacturer, setEditingManufacturer] =
    useState<ManufacturerData | null>(null);
  const [searchValue, setSearchValue] = useState("");

  // Memoize the query parameters to prevent unnecessary re-renders
  const queryParams = useMemo(
    () => ({
      remote_utility_id: remoteUtilityId.toString(),
    }),
    [remoteUtilityId]
  );

  // Use the API hooks
  const createMeterManufacturerMutation = useCreateMeterManufacturer();
  const deleteManufacturer = useDeleteManufacture();
  const updateMeterManufacturerMutation = useUpdateMeterManufacturer();
  const {
    data: manufacturersResponse,
    isLoading: isLoadingManufacturers,
    refetch: refetchManufacturers,
  } = useMeterManufacturers(queryParams);

  const manufacturers = useMemo(() => {
    if (!manufacturersResponse?.results) return [];

    return manufacturersResponse.results.map(
      (apiManufacturer): ManufacturerData => ({
        id: apiManufacturer.id.toString(),
        name: apiManufacturer.name,
        country: apiManufacturer.country,
        website: apiManufacturer.websiteUrl,
        contact: apiManufacturer.contactNumber,
        notes: apiManufacturer.description,
        createdAt: new Date(apiManufacturer.createdDate)[0],
        totalModels: 0, // This might need to come from another API call
        isActive: apiManufacturer.is_active,
        createdUserRemoteName: apiManufacturer.createdUserRemoteName,
      })
    );
  }, [manufacturersResponse]);

  // Updated form fields to match API payload structure
  const manufacturerFields: FormField[] = [
    {
      name: "name",
      label: "Manufacturer Name",
      type: "text",
      required: true,
      placeholder: "Enter manufacturer name",
      maxLength: 100,
      customValidation: (value) => {
        if (!value || value.trim().length < 2) {
          return "Manufacturer name must be at least 2 characters";
        }
        return true;
      },
      classes: {
        container: "w-full mb-2",
        label: "block text-sm font-semibold text-gray-700 mb-2",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-lg  focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-all bg-gray-50",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "country",
      label: "Country",
      type: "text",
      required: true,
      placeholder: "Enter country",
      maxLength: 50,
      classes: {
        container: "w-full mb-2",
        label: "block text-sm font-semibold text-gray-700 mb-2",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-lg  focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-all bg-gray-50",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "website_url", // Changed from "website" to match API
      label: "Website URL",
      type: "url",
      required: false,
      placeholder: "https://www.example.com",
      classes: {
        container: "w-full mb-2",
        label: "block text-sm font-semibold text-gray-700 mb-2",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-lg  focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-all bg-gray-50",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "contact_number",
      label: "Contact Number",
      type: "phone",
      required: true,
      placeholder: "Enter contact number",
      classes: {
        container: "w-full mb-2",
        label: "block text-sm font-semibold text-gray-700 mb-2",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-lg  focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-all bg-gray-50",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "description",
      label: "Description",
      type: "textarea",
      required: false,
      placeholder: "Additional information about the manufacturer",
      maxLength: 500,
      rows: 3,
      classes: {
        container: "w-full mb-6",
        label: "text-sm font-medium mb-2 block",
        textarea:
          "w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-workflow-blue focus:border-workflow-blue resize-none",
        error: "text-red-600 text-sm mt-1",
      },
    },
  ];

  // Create form for adding/editing manufacturers
  const form = FormService.createForm({
    fields: manufacturerFields,
    mode: "onChange",
    resetOnSubmit: false,
    showResetButton: false,
    showSubmitButton: false,
  });

  // Define table columns for GenericTable (keep existing structure for display)
  const tableColumns = [
    {
      key: "name",
      header: "Name",
      sortable: true,
    },
    {
      key: "country",
      header: "Country",
      sortable: true,
    },
    {
      key: "website",
      header: "Website",
      render: (value: string) => {
        if (!value) return <span className="text-gray-400">-</span>;
        return (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 underline truncate max-w-xs block"
            title={value}
          >
            {value}
          </a>
        );
      },
    },
    {
      key: "contact",
      header: "Contact",
      sortable: true,
    },
    {
      key: "notes",
      header: "Notes",
      render: (value: string) => (
        <div className="max-w-xs truncate" title={value}>
          {value || "-"}
        </div>
      ),
    },
  ];

  // Define table actions
  const tableActions  = [
    {
      label: "Edit",
      icon: "Pencil"as const,
      onClick: (row: ManufacturerData) => openEditModal(row),
    },
    { 
      label: "Delete",
      icon: "Trash2" as const,
      onClick: (row: ManufacturerData) => handleDeleteManufacturer(row.id),
      variant: "destructive" as const,
      separator: true,
    },
  ];

  // Filter manufacturers based on search
  const filteredManufacturers = manufacturers.filter(
    (manufacturer) =>
      manufacturer.name.toLowerCase().includes(searchValue.toLowerCase()) ||
      manufacturer.country.toLowerCase().includes(searchValue.toLowerCase()) ||
      manufacturer.contact.toLowerCase().includes(searchValue.toLowerCase())
  );

  // Updated handleAddManufacturer to use API
  const handleAddManufacturer = async (data: any) => {
    try {
     

      // Prepare payload according to API structure
      const payload: CreateMeterManufacturerPayload = {
        remote_utility_id: remoteUtilityId,
        name: data.name,
        country: data.country,
        website_url: data.website_url || "",
        contact_number: data.contact_number,
        description: data.description || "",
      };

     
      // Call the API using the mutation hook
      const response = await createMeterManufacturerMutation.mutateAsync(
        payload
      );

      // Refetch the manufacturers list to get updated data
      await refetchManufacturers();

      // Reset form with empty values
      form.reset({
        name: "",
        country: "",
        website_url: "",
        contact_number: "",
        description: "",
      });

      setShowAddModal(false);

    } catch (error) {

    }
  };

  const handleEditManufacturer = async (data: any) => {
    if (!editingManufacturer) return;

    try {
      

      // Prepare payload according to API structure
      const payload: UpdateMeterManufacturerPayload = {
        remote_utility_id: remoteUtilityId,
        name: data.name,
        country: data.country,
        website_url: data.website_url || "",
        contact_number: data.contact_number,
        description: data.description || "",
      };

     

      // Call the update API using the mutation hook
      await updateMeterManufacturerMutation.mutateAsync({
        manufacturerId: editingManufacturer.id,
        payload: payload,
      });

      // Refetch the manufacturers list to get updated data
      await refetchManufacturers();

      setEditingManufacturer(null);
      setShowAddModal(false);
      // Reset form with empty values
      form.reset({
        name: "",
        country: "",
        website_url: "",
        contact_number: "",
        description: "",
      });

    } catch (error) {
      
    }
  };

  const handleDeleteManufacturer = (id: string) => {
    console.log("Delete manufacturer with ID:", id);
     deleteManufacturer.mutate(Number(id));
   
  };

  const openEditModal = (manufacturer: ManufacturerData) => {
    setEditingManufacturer(manufacturer);
    setShowAddModal(true);

    // Populate form with existing data (map to API field names)
    form.reset({
      name: manufacturer.name,
      country: manufacturer.country,
      website_url: manufacturer.website,
      contact_number: manufacturer.contact,
      description: manufacturer.notes,
    });
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingManufacturer(null);
    // Reset form with empty values
    form.reset({
      name: "",
      country: "",
      website_url: "",
      contact_number: "",
      description: "",
    });
  };

  // Get loading state from both mutations
  const loading =
    createMeterManufacturerMutation.isPending ||
    updateMeterManufacturerMutation.isPending;

  const renderCustomFooter = () => (
    <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
      <button
        type="button"
        onClick={closeModal}
        disabled={loading}
        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
      >
        Cancel
      </button>
      <Button
        type="submit"
        disabled={loading}
        className="px-6 py-2 flex items-center gap-2"
      >
        {loading && (
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        )}
        {loading
          ? "Saving..."
          : editingManufacturer
          ? "Update Manufacturer"
          : "Add Manufacturer"}
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/mx/manufacturer?tab=all")}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>

              <div>
                <h1 className="page-title flex items-center gap-2">
                  Manage Manufacturers
                </h1>
                <p className="page-subtitle">
                  Add and manage meter manufacturers
                </p>
              </div>
            </div>

            {/* Add New Manufacturer Button in Header */}
            <Button
              onClick={() => {
                setEditingManufacturer(null); // Ensure we're in add mode
                // Reset form to empty values before opening modal
                form.reset({
                  name: "",
                  country: "",
                  website_url: "",
                  contact_number: "",
                  description: "",
                });
                setShowAddModal(true);
              }}
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add New Manufacturer
            </Button>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Search Bar */}
        <div className="mb-6">
          {/* Search Filter */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search manufacturers by name or country..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            />
          </div>
        </div>

        {/* GenericTable without additionalControls */}
        <GenericTable
          data={filteredManufacturers}
          columns={tableColumns}
          actions={tableActions}
          actionsType="icons"
          rowKey="id"
          loading={isLoadingManufacturers}
          emptyMessage="No manufacturers found"
        />
      </div>

      {/* Add/Edit Manufacturer Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={closeModal}
          />
          <div className="relative z-50 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingManufacturer
                    ? "Edit Manufacturer"
                    : "Add New Manufacturer"}
                </h3>
                <button
                  onClick={closeModal}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <ArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              <div className="p-6">
                <DynamicForm
                  fields={manufacturerFields}
                  form={form}
                  onSubmit={
                    editingManufacturer
                      ? handleEditManufacturer
                      : handleAddManufacturer
                  }
                  loading={loading}
                  config={{
                    layout: "vertical",
                    spacing: "comfortable",
                    showProgress: false,
                    showResetButton: false,
                    showSubmitButton: false,
                  }}
                  renderCustomFooter={renderCustomFooter}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};